import type { DifficultyRating, SolveResult, SweepDifficultyRating } from './types.js';

/**
 * Rate the difficulty of a puzzle based on its solve result.
 *
 * Factors:
 * - Grid size (from step count / deduction count)
 * - Techniques required
 * - Whether backtracking was needed
 *
 * @deprecated Use {@link rateSweepDifficulty} for sweep-count based difficulty
 * rating following Batenburg et al. (2009).
 */
export function rateDifficulty(solveResult: SolveResult): DifficultyRating {
  if (!solveResult.solved) return 'expert';

  const { techniquesUsed, usedBacktracking, steps, grid } = solveResult;

  if (usedBacktracking) return 'expert';

  const totalCells = grid.length * (grid[0]?.length ?? 0);

  // Only trivial lines needed
  if (
    techniquesUsed.size === 1 &&
    techniquesUsed.has('trivialLine')
  ) {
    return 'trivial';
  }

  // Simple overlap only (no edge logic or space filling)
  const hasEdge = techniquesUsed.has('edgeLogic');
  const hasSpace = techniquesUsed.has('spaceFilling');
  if (!hasEdge && !hasSpace) {
    return totalCells <= 50 ? 'easy' : 'medium';
  }

  // Uses edge logic or space filling
  if (hasEdge || hasSpace) {
    const stepCount = steps.length;
    if (stepCount > totalCells * 1.5) return 'hard';
    return 'medium';
  }

  return 'medium';
}

/**
 * Rate difficulty based on sweep count, following the SimpleSolver
 * approach from Batenburg et al. (2009).
 *
 * Difficulty is determined by the number of alternating h/v sweeps
 * needed to fully solve the puzzle via line-logic alone.
 *
 * @deprecated Use `SolveResult.sweepCount` directly. The raw sweep count
 * is a better difficulty measure than categorical labels.
 */
export function rateSweepDifficulty(
  solveResult: SolveResult,
  totalCells?: number,
): SweepDifficultyRating {
  if (solveResult.requiresBacktracking) return 'expert';

  const { sweepCount, grid } = solveResult;
  const cells = totalCells ?? grid.length * (grid[0]?.length ?? 0);

  // Difficulty 1 = trivial (all lines fully determined in first sweep)
  if (sweepCount <= 1) return 'trivial';
  if (sweepCount <= 3) return 'easy';
  if (sweepCount <= Math.sqrt(cells)) return 'medium';
  if (sweepCount <= cells / 2) return 'hard';
  return 'expert';
}

/**
 * Map sweep count to a numeric 0-100 score for storage.
 *
 * The score is a normalized measure of difficulty:
 * - 0 = trivial (1 sweep or fewer)
 * - 100 = expert (requires backtracking or many sweeps)
 *
 * @deprecated Use `SolveResult.sweepCount` directly. The raw sweep count
 * is a better difficulty measure than a normalized score.
 */
export function sweepCountToScore(
  sweepCount: number,
  totalCells: number,
  requiresBacktracking: boolean,
): number {
  if (requiresBacktracking) return 100;
  if (sweepCount <= 0) return 0;
  if (sweepCount === 1) return 5;

  // Scale logarithmically: sweepCount relative to sqrt(totalCells)
  // sqrt(totalCells) is the "medium" threshold
  const sqrtCells = Math.sqrt(totalCells);
  const halfCells = totalCells / 2;

  if (sweepCount <= 3) {
    // easy range: 10-30
    return 10 + Math.round(((sweepCount - 1) / 2) * 20);
  }

  if (sweepCount <= sqrtCells) {
    // medium range: 30-55
    return 30 + Math.round(((sweepCount - 3) / Math.max(1, sqrtCells - 3)) * 25);
  }

  if (sweepCount <= halfCells) {
    // hard range: 55-85
    return 55 + Math.round(
      ((sweepCount - sqrtCells) / Math.max(1, halfCells - sqrtCells)) * 30,
    );
  }

  // expert range: 85-99
  return Math.min(99, 85 + Math.round(
    ((sweepCount - halfCells) / Math.max(1, totalCells - halfCells)) * 14,
  ));
}
